<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import { createTemplatePromise } from '@vueuse/core';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';

withDefaults(
    defineProps<{
        title: string;
        description?: string;
        cancelLabel?: string;
        confirmLabel?: string;
        showCancelButton?: boolean;
        isDestructive?: boolean;
    }>(),
    {
        cancelLabel: 'Abbrechen',
        confirmLabel: 'Fortfahren',
        showCancelButton: true,
        isDestructive: false,
    }
);

const container = useTemplateRef('container');
const open = ref(true);

const TemplatePromise = createTemplatePromise<boolean>();

onMounted(() => {
    if (!container.value) return;

    const form = container.value.querySelector('form');
    form?.addEventListener('submit', async (e) => {
        e.preventDefault();

        open.value = true;

        if (await TemplatePromise.start()) {
            form.submit();
        }
    });
});
</script>

<template>
    <div
        ref="container"
        class="contents"
    >
        <slot />
    </div>
    <TemplatePromise v-slot="{ resolve }">
        <Dialog
            :default-open="true"
            v-slot="{ close }"
            @update:open="
                (isOpen) => {
                    if (!isOpen) {
                        resolve(false);
                    }
                }
            "
        >
            <DialogTrigger as-child><slot name="trigger" /></DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                    <DialogDescription v-if="description">{{
                        description
                    }}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        v-if="showCancelButton"
                        variant="outline"
                        @click="close"
                    >
                        {{ cancelLabel }}
                    </Button>
                    <Button
                        :variant="isDestructive ? 'destructive' : 'default'"
                        @click="
                            () => {
                                resolve(true);
                                close();
                            }
                        "
                    >
                        {{ confirmLabel }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </TemplatePromise>
</template>
